"use client";

import { useMemo } from "react";
import type { DetailPageGeoPricing } from "./detailPageGeoPricingTypes";
import { useCheckoutGeoPricingEligibility } from "./useCheckoutGeoPricingEligibility";
import type {
  CheckoutGeoPricingProductContext,
  DetailPricingData,
} from "./types";
import { roundToCents } from "./roundToCents";

export type { DetailPricingData } from "./types";

export type UseDetailPricingDataParams = {
  pricingData: DetailPricingData | null;
  product: CheckoutGeoPricingProductContext;
  geoPricing?: DetailPageGeoPricing;
};

export const hasGeoPricingConfig = (
  geoPricing?: DetailPageGeoPricing,
): geoPricing is DetailPageGeoPricing => {
  return geoPricing !== undefined;
};

/**
 * Applies checkout geo discount to PDP pricing when Monarch exposes `geoPricing`
 * and checkout geo is eligible. Used by both program and course detail pages.
 */
export function useDetailPricingData({
  pricingData: serverPricingData,
  geoPricing,
  product,
}: UseDetailPricingDataParams): DetailPricingData | null {
  const hasConfig = hasGeoPricingConfig(geoPricing);
  const checkoutGeoPricing = useCheckoutGeoPricingEligibility({
    enabled: hasConfig,
    product,
  });

  return useMemo(() => {
    if (!hasConfig) {
      return serverPricingData;
    }
    if (checkoutGeoPricing.isLoading || !checkoutGeoPricing.eligible) {
      return serverPricingData;
    }
    if (!serverPricingData?.originalPrice) {
      return serverPricingData;
    }

    const geoDiscountedPrice = roundToCents(
      serverPricingData.originalPrice *
        (1 - (checkoutGeoPricing.discountPercentage ?? 0) / 100),
    );
    const discountedPrice = Math.min(
      geoDiscountedPrice,
      serverPricingData.discountedPrice,
    );

    return {
      ...serverPricingData,
      discountedPrice,
      geoPricing,
      checkoutGeoPricingEligibility: checkoutGeoPricing,
    };
  }, [geoPricing, checkoutGeoPricing, hasConfig, serverPricingData]);
}
